import { GUID } from "@faro-lotv/foundation";
import { clearStore } from "@faro-lotv/project-source";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

/** State managing data exchange during alignment Wizard session */
export type AlignmentWizardToolState = {
  /** id of element which should be aligned; value is undefined when aligned element is not yet selected by user */
  elementToAlignId?: GUID;

  /** id of the point cloud stream used for alignment when the element to align contains multiple streams */
  pointCloudStreamToAlignId?: GUID;

  /** id of element which should used as reference for alignment; value is undefined when reference element is not yet selected by user */
  referenceElementId?: GUID;

  /** id of the point cloud stream used for alignment when the reference element contains multiple streams */
  referencePointCloudStreamId?: GUID;

  /** set to true if control points should be used as reference (no reference element)*/
  isControlPointsAlignment: boolean;

  /** Id of the element recommended to be used */
  recommendedElementId?: GUID;
};

const initialState: AlignmentWizardToolState = {
  isControlPointsAlignment: false,
};

const alignmentWizardModeSlice = createSlice({
  initialState,
  name: "alignmentWizardMode",
  reducers: {
    setWizardElementToAlignId(state, action: PayloadAction<GUID>) {
      state.elementToAlignId = action.payload;
    },

    setWizardPointCloudStreamToAlignId(
      state,
      action: PayloadAction<GUID | undefined>,
    ) {
      state.pointCloudStreamToAlignId = action.payload;
    },

    setWizardReferenceElementId(state, action: PayloadAction<GUID>) {
      state.referenceElementId = action.payload;
    },

    setWizardReferencePointCloudStreamId(
      state,
      action: PayloadAction<GUID | undefined>,
    ) {
      state.referencePointCloudStreamId = action.payload;
    },

    setWizardRecommendedElementId(
      state,
      action: PayloadAction<GUID | undefined>,
    ) {
      state.recommendedElementId = action.payload;
    },

    setControlPointsAlignmentEnabled(state, action: PayloadAction<boolean>) {
      state.isControlPointsAlignment = action.payload;
      if (state.isControlPointsAlignment) {
        state.referenceElementId = undefined;
      }
    },

    resetWizardReferenceElementId(state) {
      state.referenceElementId = undefined;
    },

    resetAlignmentWizard() {
      return {
        ...initialState,
      };
    },
  },

  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const {
  setWizardElementToAlignId,
  setWizardPointCloudStreamToAlignId,
  setWizardReferenceElementId,
  setWizardReferencePointCloudStreamId,
  setWizardRecommendedElementId,
  setControlPointsAlignmentEnabled,
  resetWizardReferenceElementId,
  resetAlignmentWizard,
} = alignmentWizardModeSlice.actions;

export const alignmentWizardModeReducer = alignmentWizardModeSlice.reducer;
